// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { User } from '@supabase/supabase-js';

// Generate a secure nonce for Apple authentication
function generateSecureNonce(): string {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  error?: string;
}

export interface AuthService {
  signupWithEmail(
    email: string, 
    password: string, 
    metadata?: { full_name: string }
  ): Promise<AuthResponse>;
  signInWithEmail(
    email: string,
    password: string
  ): Promise<AuthResponse>;
  loginWithEmail(
    email: string,
    password: string
  ): Promise<AuthResponse>;
  // ... other methods ...
}

export const authService = {
  async checkConnection(): Promise<boolean> {
    try {
      const { error } = await supabase.from('profiles').select('id').limit(1);
      return !error;
    } catch (err) {
      console.error('Supabase connection error:', err);
      return false;
    }
  },

  async signupWithEmail(
    email: string,
    password: string,
    metadata?: { full_name: string }
  ): Promise<AuthResponse> {
    try {
      // Try to sign in first to check if user exists
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (!signInError && signInData?.user) {
        return { success: true, user: signInData.user };
      }
      
      // User doesn't exist, create a new account
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata || { full_name: email.split('@')[0] },
        }
      });
      
      if (error) {
        return { success: false, error: error.message };
      }
      
      return { 
        success: true, 
        user: data.user || undefined
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : "Unknown error during signup" 
      };
    }
  },
  
  // Helper method to ensure profile records exist
  async ensureUserProfileExists(
    userId: string,
    email: string,
    fullName: string
  ): Promise<void> {
    // 1. Check/Create profile
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();
      
    if (!existingProfile) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: email,
          full_name: fullName,
          display_name: fullName,
          photo_url: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
      if (profileError) {
        // Error creating profile, but continue with other setup
      }
    }
    
    // 2. Check/Create notification settings
    const { data: existingSettings } = await supabase
      .from('notification_settings')
      .select('id')
      .eq('id', userId)
      .single();
      
    if (!existingSettings) {
      // Create notification settings with defaults
      await supabase
        .from('notification_settings')
        .insert({
          id: userId,
          transaction_created: true,
          transaction_deleted: true,
          large_transactions: true,
          recurring_transactions: true,
          category_changed: true,
          transfer_complete: true,
          exchange_rate_updates: true,
          new_features: true,
          app_updates: true,
          security_alerts: true,
          sync_status: true,
          budget_approaching: true,
          budget_exceeded: true,
          budget_reset: true,
          show_in_app: true,
          push_notifications: true,
          sound_enabled: true,
          vibration_enabled: true,
          updated_at: new Date().toISOString()
        });
    }
    
    // 3. Check/Create user preferences
    const { data: existingPreferences } = await supabase
      .from('user_preferences')
      .select('id')
      .eq('id', userId)
      .single();
      
    if (!existingPreferences) {
      // First try with regular insert
      const { error: preferencesError } = await supabase
        .from('user_preferences')
        .insert({
          id: userId,
          user_id: userId,
          theme: 'system',
          language: 'en',
          currency: 'USD',
          date_format: 'MM/DD/YYYY',
          time_format: 'hh:mm A',
          number_format: '1,000.00',
          timezone: 'UTC',
          notifications_enabled: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
      if (preferencesError) {
        // Try with raw SQL as fallback
        try {
          await supabase.rpc('insert_user_preference_raw', {
            user_id_param: userId
          });
        } catch (sqlError) {
          // Error with raw SQL insert, but continue with auth flow
        }
      }
    }
  },

  async signInWithEmail(
    email: string,
    password: string
  ): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // Handle profiles on login if they don't exist
        try {
          await ensureUserProfile(data.user.id, 
            data.user.user_metadata?.full_name || email.split('@')[0], 
            email);
        } catch (profileError) {
          // Continue even if profile creation fails
        }
        return { success: true, user: data.user };
      }

      return { success: false, error: "Unknown login error" };
    } catch (err) {
      return { 
        success: false, 
        error: err instanceof Error ? err.message : "Unknown error during login" 
      };
    }
  },

  // Add loginWithEmail as an alias to signInWithEmail for backward compatibility
  async loginWithEmail(
    email: string,
    password: string
  ): Promise<AuthResponse> {
    return this.signInWithEmail(email, password);
  },

  async loginWithGoogle(): Promise<AuthResponse> {
    try {
      // Check if we have any existing session that might be interfering
      const { data: sessionData } = await supabase.auth.getSession();
      
      // If we already have a valid session, redirect directly to dashboard
      if (sessionData.session) {
        window.location.replace('/app/dashboard');
        return { success: true };
      }
      
      // Get the current origin for the redirectTo URL
      const origin = window.location.origin;
      const callbackUrl = `${origin}/auth/callback`;
      
      // Clean up any stale auth flags before starting new auth flow
      sessionStorage.removeItem('auth_in_progress');
      sessionStorage.removeItem('auth_attempt_count');
      sessionStorage.removeItem('auth_completed');
      
      // Use sessionStorage - it will automatically clear when the browser is closed
      sessionStorage.setItem('auth_in_progress', 'true');
      
      // Store current timestamp to help detect stale flags
      sessionStorage.setItem('auth_start_time', Date.now().toString());
      
      // Use Supabase's built-in OAuth flow with the app's callback
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
          queryParams: {
            auth_source: 'flows_app',
            auth_time: Date.now().toString()
          }
        }
      });
      
      if (error) {
        sessionStorage.removeItem('auth_in_progress');
        throw error;
      }
      
      // For OAuth providers, this will redirect the user
      return { success: true };
    } catch (error: any) {
      sessionStorage.removeItem('auth_in_progress');
      return {
        success: false,
        error: error.message
      };
    }
  },

  async signInWithIdToken(provider: 'google' | 'apple', token: string, nonce?: string): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider,
        token,
        nonce
      });
      
      if (error) throw error;
      
      return { success: true, user: data.user };
    } catch (error: any) {
      return {
        success: false,
        error: error.message
      };
    }
  },

  async loginWithApple(): Promise<AuthResponse> {
    try {
      // Generate a secure nonce for Apple authentication
      const nonce = generateSecureNonce();
      
      // Store the nonce in session storage to verify it when Apple redirects back
      sessionStorage.setItem('appleAuthNonce', nonce);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            // Pass the nonce to Apple
            nonce: nonce,
            // Request user's name and email
            scope: 'name email'
          }
        }
      });
      
      if (error) throw error;
      
      // Note: For OAuth providers, this will redirect the user and not return a user immediately
      return { success: true };
    } catch (error: any) {
      console.error('Apple sign-in error:', error);
      
      return {
        success: false,
        error: error.message
      };
    }
  },

  async logout(): Promise<void> {
    await supabase.auth.signOut();
  },

  async resetPassword(email: string, redirectTo?: string): Promise<AuthResponse> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        email,
        {
          redirectTo: redirectTo || `${window.location.origin}/auth/reset-password`
        }
      );
      
      if (error) {
        return { 
          success: false, 
          error: error.message
        };
      }
      
      return { 
        success: true 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : "Unknown error sending magic link" 
      };
    }
  },

  /**
   * Handles passwordless login using OTP/magic link
   */
  async signInWithMagicLink(
    email: string,
    redirectTo?: string
  ): Promise<AuthResponse> {
    try {
      console.log(`Sending magic link to: ${email}`);
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: redirectTo || `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        console.error('Magic link error:', error);
        return { 
          success: false, 
          error: error.message
        };
      }
      
      console.log('Magic link sent successfully');
      return { 
        success: true 
      };
    } catch (error) {
      console.error('Unexpected magic link error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : "Unknown error sending magic link" 
      };
    }
  }
};

// Simplified profile creation logic focused on SQL approach
async function ensureUserProfile(
  userId: string,
  fullName: string,
  email: string
): Promise<void> {
  try {
    // Skip all the complex profile creation attempts
    // Only use SQL for ultimate reliability
    await supabase.rpc('create_user_profile', {
      user_id_param: userId,
      email_param: email,
      full_name_param: fullName
    });
  } catch (error) {
    // Don't rethrow - we want to continue even if this fails
  }
}
